import {inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BehaviorSubject, finalize, firstValueFrom, merge} from 'rxjs';
import {IDivisionFilter, ISplitStats} from '../models/division.model';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {AuthService} from '@core/authentication/auth.service';
import {ROLES_NEW} from '@core/authentication/auth.constants';
import {ROLES} from '@shared/models/user/user.model';
import {IConversation} from '../models/conversation.model';
import {
  ASSIGNEE_ID_ALL_STAFF_VIEW,
  ASSIGNEE_ID_STOP_SPLIT,
  SYSTEM_ASSIGNEE_IDS,
} from '../models/conversation-constants.model';
import {environment} from '@environments/environment';

export interface IAssignee {
  id: string;
  name: string;
  avatar?: string;
  online?: boolean;
}

export interface IDivisionSettings {
  mode?: string;
  autoAssign?: boolean;
  maxConversationsPerStaff?: number;
  pageIds?: string[];
}

/** Một cấu hình "chia cho nhân viên khác" có áp dụng cho user đang đăng nhập (FE13 changeOtherData). */
export interface IChangeOtherPermissionItem {
  pageIds?: Array<string | number>;
  receiveIds?: Array<string | number>;
}

/** Quyền chia hội thoại cho nhân viên khác, gom theo businessId — response /business/index/assignablereceivers. */
export interface IChangeOtherData {
  [businessId: string]: IChangeOtherPermissionItem[];
}

/** Nhân viên trong popup "Tìm theo nhân viên được phân công" — response /business/user/manageusersplit. */
export interface IManageSplitUser {
  id: string;
  roleId?: string;
  display?: number | string;
  userDisplayNamePos?: string;
  userDisplayName?: string;
}

/**
 * Quản trị chia số/assign hội thoại cho nhân viên/nhóm:
 * - filter cấu hình hiển thị/thống kê
 * - stats$ để UI subscribe bảng thống kê
 * - VIEW_TYPE để chuyển đổi table/list
 */
@Injectable({providedIn: 'root'})
export class SplitConversationService {
  private readonly api = inject(ConversationApiService);
  private readonly authService = inject(AuthService);

  public VIEW_TYPE = {LIST: 1, TABLE: 2};
  public filter: IDivisionFilter = {};
  public viewTypeCurrent = this.VIEW_TYPE.LIST;
  /** FE13 parity: đang mở tab quản lý chia hội thoại trong right panel */
  public inViewAvgSplit = false;
  public assignees: IAssignee[] = [];
  public divisionSettings: IDivisionSettings | null = null;

  private _stats$ = new BehaviorSubject<ISplitStats[]>([]);
  public stats$ = this._stats$.asObservable();

  /** FE13 parity: BehaviorSubject để reload manage split tab */
  public _reloadManageSplit = new BehaviorSubject<boolean>(false);

  /** FE13 parity: emit reload signal cho manage-split-tab */
  public reloadManageSplit(bool: boolean): void {
    this._reloadManageSplit.next(bool);
  }

  constructor() {
    // Phiên đăng nhập bị xóa (đăng xuất / lỗi phiên) → bỏ quyền chia của user cũ,
    // tránh tài khoản đăng nhập sau trong cùng tab dùng nhầm khi API nạp quyền lỗi.
    this.authService.onAuthCleared$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.shareConversationPermissionSeq++;
      this.shareConversationPermissionRequest = null;
      this._changeOtherData.set({});
      this._pageManageCount.set(0);
      this.splitStatus.set(false);
    });

    // FE13 limitPermission = authService.isAllowed(ROLES.Business_Manager), tính lại khi đổi doanh nghiệp / phiên.
    // Cache vào signal vì isAllowed() đọc/ghi localStorage — không gọi trực tiếp mỗi lần render template.
    // activeBusiness$ phát trước khi localStorage cập nhật, onAuthChanged$ phát sau → nghe cả hai để luôn đúng.
    merge(this.authService.activeBusiness$, this.authService.onAuthChanged$)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this._limitPermission.set(this.authService.isAllowed(ROLES.Business_Manager)));
  }

  /** Signal: chế độ chia hội thoại hiện tại. */
  public readonly openModeSplit = signal<boolean>(false);
  /** Signal: danh sách ID hội thoại đã chọn để chia. */
  public readonly selectedConvIds = signal<string[]>([]);
  /** Signal: checkbox Chọn tất cả. */
  public readonly isCheckAll = signal<boolean>(false);

  /** Bật/tắt chế độ chia. Reset selection khi tắt. */
  public toggleSplitMode(): void {
    const next = !this.openModeSplit();
    this.openModeSplit.set(next);
    if (!next) {
      this.selectedConvIds.set([]);
      this.isCheckAll.set(false);
    }
  }

  /** Đóng chế độ chia hội thoại — reset toàn bộ state. */
  public closeSplitMode(): void {
    this.openModeSplit.set(false);
    this.selectedConvIds.set([]);
    this.isCheckAll.set(false);
    this._selectedBusinessId = 0;
  }

  /** FE13 parity: toggle giữa chế độ xem LIST (right panel) và TABLE (toàn màn hình) */
  public toggleViewSplitManage(): void {
    this.viewTypeCurrent = this.viewTypeCurrent === this.VIEW_TYPE.TABLE
      ? this.VIEW_TYPE.LIST
      : this.VIEW_TYPE.TABLE;
  }

  /** Business ID đang được chọn để chia hội thoại. */
  private _selectedBusinessId = 0;
  public get selectedBusinessId(): number {
    return this._selectedBusinessId;
  }

  /** Bật split mode cho 1 business cụ thể + lọc danh sách theo pageIds. */
  public selectBusinessForSplit(businessId: number, pageIds: string[]): void {
    this._selectedBusinessId = businessId;
    this.openModeSplit.set(true);
    this.selectedConvIds.set([]);
    this.isCheckAll.set(false);
  }

  /** Chọn / Bỏ chọn tất cả hội thoại trong danh sách. */
  public toggleSelectAll(checked: boolean, allIds: string[]): void {
    this.isCheckAll.set(checked);
    this.selectedConvIds.set(checked ? [...allIds] : []);
  }

  /** Chọn / Bỏ chọn 1 hội thoại khi ở chế độ chia */
  public toggleSelectConversation(convId: string): void {
    const current = this.selectedConvIds();
    const idx = current.indexOf(convId);
    if (idx > -1) {
      this.selectedConvIds.set(current.filter(id => id !== convId));
      this.isCheckAll.set(false);
    } else {
      this.selectedConvIds.set([...current, convId]);
    }
  }

  /** Kiểm tra hội thoại có đang được chọn chia hay không */
  public isConversationSelected(convId: string): boolean {
    return this.selectedConvIds().includes(convId);
  }

  /**
   * Cập nhật dữ liệu thống kê chia số
   */
  public setStats(items: ISplitStats[]): void {
    this._stats$.next(items || []);
  }

  /**
   * Chia nhiều hội thoại cho một nhân viên cụ thể.
   * Hàm này được thiết kế để FE gọi từ màn chọn hàng loạt hội thoại.
   *
   * @param conversationIds danh sách ID hội thoại cần chia.
   * @param assigneeId ID nhân viên nhận hội thoại.
   */
  public async splitConversations(conversationIds: string[], assigneeId: string): Promise<boolean> {
    // Bước 1: Kiểm tra tính hợp lệ của dữ liệu đầu vào
    // Nếu danh sách ID trống hoặc không có nhân viên nhận thì không gọi API để tối ưu tài nguyên
    if (!conversationIds.length || !assigneeId) return false;

    // Bước 2: Gọi API thực hiện gán (Re-assign) hàng loạt hội thoại cho nhân viên
    const res = await firstValueFrom(this.api.splitConversations({
      conversationIds,
      assigneeId
    }));

    // Trả về true nếu code thành công (thường là 1)
    return res?.code === 1;
  }

  /**
   * Tải cấu hình chia hội thoại hiện tại của doanh nghiệp.
   * Ví dụ: mode chia, ưu tiên, danh sách page áp dụng.
   */
  public async loadDivisionSettings(): Promise<IDivisionSettings> {
    const res = await firstValueFrom(this.api.getDivisionSettings({}));
    this.divisionSettings = ((res?.data || {}) as IDivisionSettings);

    // Populate pageSplitIds từ response (FE13: this.splitData = res.fanpageSplitIds)
    const splitIds = (res?.data as any)?.fanpageSplitIds;
    if (Array.isArray(splitIds)) {
      this.setPageSplitIds(splitIds);
    }

    return this.divisionSettings;
  }

  /**
   * Lấy danh sách nhân viên có thể được phân công hội thoại.
   * Dữ liệu trả về phục vụ popup/chọn người nhận hội thoại.
   */
  public async getAssigneeList(): Promise<IAssignee[]> {
    // Bước 1: Gọi API lấy danh sách nhân viên có quyền được gán hội thoại (Assignable)
    const res = await firstValueFrom(this.api.getAssignableReceivers({}));

    // Ép kiểu dữ liệu trả về về dạng mảng thô (unknown[])
    const rows = ((res?.data as any) || []);

    // Bước 2: Duyệt qua danh sách và map về Interface IAssignee chuẩn của FE
    this.assignees = rows.map((row: any) => {
      const item = (row || {}) as Record<string, unknown>;
      return {
        // Hỗ trợ linh hoạt: Map cả 'userId'/'fullName' (cũ) và 'id'/'name' (mới) từ backend
        id: String(item['id'] || item['userId'] || ''),
        name: String(item['name'] || item['fullName'] || ''),
        avatar: String(item['avatar'] || ''),
        // Chuyển đổi về kiểu Boolean để UI xử lý logic online/offline
        online: Boolean(item['online'])
      };
    });

    return this.assignees;
  }

  /**
   * Nạp quyền chia hội thoại cho nhân viên khác (changeOtherData).
   * Parity FE13: conversation.component.ts getBusinessPagesAndUserIds() → POST /business/index/assignablereceivers.
   * Gọi khi vào màn hội thoại: desktop (ConversationLayoutComponent) và mobile (ConversationMobileShellComponent).
   *
   * @param maxAgeMs > 0: dữ liệu của phiên hiện tại vừa nạp thành công trong khoảng này thì dùng lại (mobile dựng lại
   *                 shell mỗi lần quay về từ trang lọc/thao tác).
   * @returns true nếu nạp thành công
   */
  public loadShareConversationPermission(maxAgeMs = 0): Promise<boolean> {
    // Bước 1: Đang có request chạy thì dùng chung, không gọi API lặp
    if (this.shareConversationPermissionRequest) {
      return this.shareConversationPermissionRequest;
    }
    const loaded = this.shareConversationPermissionLoaded;
    if (maxAgeMs > 0 && loaded?.seq === this.shareConversationPermissionSeq && Date.now() - loaded.at < maxAgeMs) {
      return Promise.resolve(true);
    }

    // Bước 2: Gọi API — giống FE13, chỉ ghi đè dữ liệu khi API trả thành công
    const seq = this.shareConversationPermissionSeq;
    const request = firstValueFrom(this.api.getAssignableReceivers({}))
      .then(res => {
        // Phiên đã bị xóa trong lúc chờ response → bỏ kết quả của phiên cũ
        if (seq !== this.shareConversationPermissionSeq || res?.code !== 1) return false;
        const data: unknown = res.data;
        // BE trả object { [businessId]: [{pageIds, receiveIds}] }; không có quyền thì PHP có thể trả [] → coi như rỗng
        this._changeOtherData.set(data && typeof data === 'object' && !Array.isArray(data) ? data as IChangeOtherData : {});
        this.shareConversationPermissionLoaded = {seq, at: Date.now()};
        return true;
      })
      .catch(() => false)
      .finally(() => {
        if (this.shareConversationPermissionRequest === request) {
          this.shareConversationPermissionRequest = null;
        }
      });
    this.shareConversationPermissionRequest = request;
    return request;
  }

  /**
   * Danh sách nhân viên cho popup lọc "Tìm theo nhân viên được phân công".
   * Parity FE13 DivisionFilterComponent.getAllUser(): POST /business/user/manageusersplit
   * { businessIds: getBusinessAllSelectManagement(), limit: 250 } — BE chỉ trả nhân viên user được quản lý chia,
   * bỏ trùng theo id; chỉ giữ người hiển thị được (không phải SuperAdmin/Admin/Giám đốc, display != 0, có tên).
   *
   * @returns danh sách nhân viên; null nếu API lỗi để nơi gọi dùng nguồn dữ liệu cũ
   */
  public async loadUsersManageSplit(): Promise<IManageSplitUser[] | null> {
    // Bước 1: Gọi API giống FE13
    const businessIds = this.authService.getBusinessAllSelectManagement();
    const res: any = await firstValueFrom(this.api.getUserManageSplit({businessIds, limit: 250})).catch(() => null);
    if (!res || res.code !== 1) return null;

    // Bước 2: FE13 đọc res.items — sau normalizeV1Response có thể nằm ở data.items hoặc data
    const rawItems: unknown = Array.isArray(res.data?.items) ? res.data.items
      : Array.isArray(res.items) ? res.items
        : res.data;
    if (!Array.isArray(rawItems)) return null;

    // Bước 3: Bỏ trùng theo id (FE13: new Map(items.map(item => [item.id, item])) — bản ghi sau ghi đè bản ghi trước)
    const uniqueUsers = new Map<string, IManageSplitUser>();
    rawItems.forEach((item: any) => {
      const id = item?.id != null ? String(item.id) : '';
      if (id) uniqueUsers.set(id, {...item, id});
    });

    // Bước 4: Điều kiện hiển thị như template FE13
    const hiddenRoles: string[] = [ROLES_NEW.SYSTEM_ROLE_SUPER_ADMIN, ROLES_NEW.SYSTEM_ROLE_ADMIN, ROLES_NEW.SYSTEM_ROLE_MANAGER];
    return [...uniqueUsers.values()].filter(user => {
      // FE13 `user.display != 0` (so sánh lỏng): ẩn khi display là 0 / '0' / '' / false
      const display: unknown = user.display;
      const isHiddenByDisplay = display === 0 || display === '0' || display === '' || display === false;
      return !hiddenRoles.includes(String(user.roleId ?? ''))
        && !isHiddenByDisplay
        && !!(user.userDisplayNamePos || user.userDisplayName);
    });
  }

  // ── Private Reply Split ──────────────────────────────────────────────────

  /** FE13 splitData equivalent — page IDs có bật chế độ chia hội thoại. */
  public pageSplitIds: string[] = [];
  /** FE13 splitData.pageManageIds — page user có quyền chia hội thoại cho người khác. */
  public pageManageIds: string[] = [];
  /** FE13 splitData.leaderPageIds — page user có quyền leader. */
  public leaderPageIds: string[] = [];
  /** FE13 splitData.leaderGroupIds — user có quyền leader 1 nhóm. */
  public leaderGroupIds: Record<string, { groupIds?: string[] }> | null = null;
  /** FE13 splitData.modeByBusiness — chế độ chia theo Id doanh nghiệp. */
  public modeByBusiness: Record<string, number> = {};
  /** FE13 splitData.pageAllIds — page user được xem tất cả hội thoại. */
  public pageAllIds: string[] = [];
  /**
   * FE13 changeOtherData — user có quyền chia cho người khác (theo businessId).
   * Lưu bằng signal để component OnPush (conversation-item, conversation-row) tự vẽ lại khi quyền nạp xong.
   */
  private readonly _changeOtherData = signal<IChangeOtherData>({});
  public get changeOtherData(): IChangeOtherData {
    return this._changeOtherData();
  }
  /** Request nạp changeOtherData đang chạy — dùng chung để không gọi API trùng. */
  private shareConversationPermissionRequest: Promise<boolean> | null = null;
  /** Lượt nạp quyền chia thành công gần nhất (theo seq của phiên đăng nhập). */
  private shareConversationPermissionLoaded: {seq: number; at: number} | null = null;
  /** Tăng mỗi khi phiên đăng nhập bị xóa — bỏ response trả về muộn của phiên cũ. */
  private shareConversationPermissionSeq = 0;
  /** Số page user có quyền chia thủ công (pageManageIds) — signal để UI OnPush/computed cập nhật khi list trả về. */
  private readonly _pageManageCount = signal(0);
  /** FE13 limitPermission (Giám đốc trở lên theo doanh nghiệp đang chọn) — cập nhật trong constructor. */
  private readonly _limitPermission = signal(false);

  /**
   * FE13 `limitPermission || splitData.pageManageIds.length`:
   * quyền lọc "Tìm theo nhân viên được phân công" và mở "Chế độ chia hội thoại".
   * Chỉ đọc signal → rẻ khi gọi trong template, tự cập nhật khi đổi doanh nghiệp hoặc list trả về.
   */
  public canManageSplit(): boolean {
    return this._limitPermission() || this._pageManageCount() > 0;
  }
  /** FE13 splitData.status — user có quyền giám đốc hiển thị view manage. */
  public readonly splitStatus = signal(false);

  /**
   * Cập nhật danh sách page có bật split (gọi từ loadDivisionSettings hoặc init).
   */
  public setPageSplitIds(ids: string[]): void {
    this.pageSplitIds = ids || [];
  }

  /**
   * Gọi API chia hội thoại inbox cho nhân viên vừa trả lời private reply.
   * Tương đương FE13: SplitConversationService.handlePrivateReply()
   * Ưu tiên chia luôn cho nhân viên đang trả lời comment.
   */
  public handlePrivateReply(conversationId: string): void {
    if (!conversationId) return;
    setTimeout(() => {
      this.api.splitPrivateReply({conversationId}).subscribe({
        next: () => {
        },
        error: () => {
        },
      });
    }, 300);
  }

  // ── Permission checks (migrated from FE13) ───────────────────────────

  /**
   * Populate split data từ API response (FE13: setDataFromConversation).
   * Được gọi sau khi fetch conversation list — response chứa split metadata.
   */
  public setDataFromConversation(res: any): void {
    if (!res) return;

    if (Array.isArray(res.fanpageAllIds)) {
      // Chuẩn hóa ID pageAllIds về dạng string để tránh type mismatch khi includes
      this.pageAllIds = res.fanpageAllIds.map(String);
    }
    if (Array.isArray(res.fanpageSplitIds)) {
      // Chuẩn hóa ID pageSplitIds về dạng string để tránh type mismatch khi includes
      this.pageSplitIds = res.fanpageSplitIds.map(String);
    }
    if (Array.isArray(res.pageManageIds)) {
      // Chuẩn hóa ID pageManageIds về dạng string để tránh type mismatch khi includes
      this.pageManageIds = res.pageManageIds.map(String);
      this._pageManageCount.set(this.pageManageIds.length);
    }
    if (Array.isArray(res.leaderPageIds)) {
      // Chuẩn hóa ID leaderPageIds về dạng string để tránh type mismatch khi includes
      this.leaderPageIds = res.leaderPageIds.map(String);
    }
    if (res.leaderGroupIds) {
      this.leaderGroupIds = res.leaderGroupIds;
    }
    if (res.modeByBusiness) {
      this.modeByBusiness = res.modeByBusiness;
    }
    // FE13: splitData.status = res.statusSplit (user có quyền giám đốc && hiển thị view manage).
    // Không suy ra từ fanpageSplitIds/pageManageIds/modeByBusiness như trước vì nhân viên được chia
    // hội thoại cũng có các field này → tab "Quản lý chia hội thoại" hiện sai cho nhân viên.
    // Response không có statusSplit → giữ giá trị hiện tại (không tự bật, không làm mất tab đang hiện).
    if (res.statusSplit !== undefined) {
      this.splitStatus.set(!!res.statusSplit);
    }
  }

  /**
   * Check user đang đăng nhập có quyền thay đổi nhân viên xử lý hội thoại không?
   * Migrated từ FE13 SplitConversationService.hasChangeAssignee().
   *
   * Logic 4 tầng:
   * 1. User là Super Admin / Admin / Manager của business sở hữu page
   * 2. Page nằm trong pageManageIds (quyền chia thủ công)
   * 3. User có cấu hình changeOtherData cho page này
   * 4. User là leader của nhóm đang gán cho hội thoại
   */
  public hasChangeAssignee(conversation: IConversation | {
    fanpageId?: string;
    assignedGroupId?: string
  } | null): boolean {
    const pageId = conversation?.fanpageId;
    if (!pageId) return false;

    // Tầng 1: Quản lý business
    if (this.checkManageBusiness(pageId)) return true;

    // Tầng 2: Quyền chia thủ công
    if (this.pageManageIds?.includes(pageId)) return true;

    // Tầng 3: Cấu hình changeOtherData (nạp qua loadShareConversationPermission)
    // FE13: authService.getBussinessId(pageId) tra trên toàn bộ auth.pages — danh sách hội thoại
    // gồm page của mọi doanh nghiệp, không chỉ doanh nghiệp đang chọn ở header.
    const businessId = this.authService.getBussinessId(pageId);
    if (businessId) {
      const data = this.changeOtherData[businessId];
      if (Array.isArray(data)) {
        for (const item of data) {
          // pageIds trong cấu hình chia có thể là number hoặc string → so sánh theo chuỗi
          const hasPage = item.pageIds?.some(id => String(id) === String(pageId));
          if (hasPage && item.receiveIds?.length) {
            return true;
          }
        }
      }
    }

    // Tầng 4: Leader nhóm
    if (this.isAllowManageGroup(conversation as any)) return true;

    return false;
  }

  /**
   * Kiểm tra user có phải quản lý (Super Admin / Admin / Manager) của business chứa page.
   * Migrated từ FE13 SplitConversationService.checkManageBusiness().
   * Public để ConversationListService dùng trong limitPermission check.
   */
  public checkManageBusiness(pageId: string): boolean {
    if (!pageId) return false;
    const businessId = this.getBusinessIdByPageId(pageId);
    const businesses = this.authService.getBusinessesManagement();
    const bss = businesses.find((b: any) => String(b.id) === String(businessId));
    if (bss) {
      const roleId = (bss as any).roleId ?? '';
      if (roleId && [
        ROLES_NEW.SYSTEM_ROLE_SUPER_ADMIN,
        ROLES_NEW.SYSTEM_ROLE_ADMIN,
        ROLES_NEW.SYSTEM_ROLE_MANAGER
      ].includes(roleId)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Lấy danh sách ID nhân viên thuộc các nhóm của Leader (GAP 1)
   */
  public getStaffIdsOfLeaderGroups(groupIds: (string | number)[], groupsByBusiness?: Record<string, any[]>): string[] {
    const ids = new Set<string>();
    if (!groupIds?.length || !groupsByBusiness) return [];
    Object.values(groupsByBusiness).forEach(groupList => {
      if (Array.isArray(groupList)) {
        groupList.forEach((g: any) => {
          const gid = String(g.id ?? g.groupId ?? '');
          if (groupIds.map(String).includes(gid) && Array.isArray(g.userIds)) {
            g.userIds.forEach((uId: any) => ids.add(String(uId)));
          }
        });
      }
    });
    return Array.from(ids);
  }

  /**
   * Kiểm tra user có quyền leader nhóm đang gán cho hội thoại.
   * Migrated từ FE13 SplitConversationService.isAllowManageGroup().
   * Public để ConversationListService dùng trong hasSplitPage check.
   */
  public isAllowManageGroup(conversation: { fanpageId?: string; assignedGroupId?: string }): boolean {
    const pageId = conversation?.fanpageId;
    if (!pageId || !this.leaderGroupIds) return false;

    const pageLeaders = this.leaderGroupIds[pageId];
    if (pageLeaders?.groupIds && conversation?.assignedGroupId) {
      if (pageLeaders.groupIds.includes(conversation.assignedGroupId)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check có hiển thị button sửa group (thay vì nhân viên) không.
   * Migrated từ FE13 SplitConversationService.isShowChangeGroup().
   */
  public isShowChangeGroup(conversation: { fanpageId?: string } | null): boolean {
    if (!conversation?.fanpageId) return false;
    const mode = this.getModeSplitByPageId(conversation.fanpageId);
    return mode === 2; // mode 2: setting theo group
  }

  /**
   * Kiểm tra record hội thoại có nằm trong page được chia không.
   * Port từ FE13 `conversation.service.ts hasSplitPage()`.
   * Nếu page này không bật split → return false (không cần check assignee).
   * Nếu có split → return true để caller quyết định có check assignee không.
   */
  public hasSplitPage(conversation: { fanpageId?: string; ops?: { ignoreSplit?: boolean } }): boolean {
    if (conversation?.ops?.ignoreSplit) return false;
    if (!conversation?.fanpageId) return false;

    const pageId = String(conversation.fanpageId);

    // User có quyền chia thủ công page → bỏ qua
    if (this.pageManageIds.map(String).includes(pageId)) return false;
    // User thuộc page all → xem được tất cả
    if (this.pageAllIds.map(String).includes(pageId)) return false;
    // User là leader của nhóm → skip check
    if (this.isAllowManageGroup(conversation)) return false;

    // Còn lại — luôn check chia
    return true;
  }

  /**
   * Kiểm tra user hiện tại có nằm trong danh sách assigneeIds của hội thoại không.
   * Port từ FE13 `conversation.service.ts hasAllowAssignee()`.
   * Nếu không thuộc → xóa hội thoại khỏi list (và reset chat nếu đang xem).
   * @returns true nếu user được assign, false nếu không (đã xóa khỏi list)
   */
  public hasAllowAssignee(
    conversation: { fanpageId?: string; assigneeIds?: string[]; id?: string },
  ): boolean {
    // [FIX] Admin/SuperAdmin KHÔNG bị check chia hội thoại — luôn thấy tất cả
    // Phải để ĐẦU TIÊN, trước mọi check pageId/assigneeIds
    if (this.authService.isAdmin()) {
      return true;
    }

    if (!Object.prototype.hasOwnProperty.call(conversation, 'assigneeIds')) {
      // Không có field assigneeIds → không check chia
      return true;
    }
    if (!conversation?.fanpageId) return false;

    const pageId = conversation.fanpageId;
    if (
      !this.pageAllIds.includes(pageId) &&
      !this.pageSplitIds.includes(pageId) &&
      !this.pageManageIds.includes(pageId)
    ) {
      // User không có quyền xem các page này
      return false;
    }

    const assigneeIds = Array.isArray(conversation.assigneeIds) ? conversation.assigneeIds : [];
    const uid = String(this.authService.getUid() ?? '');
    // Chuẩn hóa toàn bộ ID về dạng string để tránh type mismatch giữa number và string từ socket Backend
    const stringAssigneeIds = assigneeIds.map(String);

    // Cho phép nếu nhân viên đăng nhập có ID trong assigneeIds HOẶC hội thoại có cờ all_staff_view cho phép tất cả NV page xem
    if (stringAssigneeIds.includes(uid) || stringAssigneeIds.includes(ASSIGNEE_ID_ALL_STAFF_VIEW)) {
      return true;
    }

    // User không được chia hội thoại này → cần xóa khỏi list
    // NOTE: Việc xóa khỏi list + resetChat do caller (ConversationListService) xử lý
    return false;
  }

  /**
   * Kiểm tra hội thoại mới đến có đủ điều kiện để add vào list không.
   * Port từ FE13 `conversation.service.ts isAddNewConversationSocket()`.
   * Tránh trường hợp nhân viên xem được hội thoại không được chia cho mình
   * (một số socket bắn thiếu trường assigneeIds).
   */
  public isAddNewConversationSocket(conversation: { fanpageId?: string; assigneeIds?: string[] }): boolean {
    if (!conversation?.fanpageId) return false;

    if (this.pageSplitIds.includes(conversation.fanpageId)) {
      // Page có chia hội thoại — chỉ add nếu có assigneeIds && user được assign
      if (!Object.prototype.hasOwnProperty.call(conversation, 'assigneeIds') || !Array.isArray(conversation.assigneeIds)) {
        return false;
      }
      // Cho phép thêm hội thoại nếu được gán cho user login hoặc hội thoại cho phép tất cả NV page xem
      return conversation.assigneeIds.includes(this.authService.getUid()) ||
             conversation.assigneeIds.includes(ASSIGNEE_ID_ALL_STAFF_VIEW);
    }
    return true;
  }

  /**
   * Kiểm tra hội thoại đến từ socket có được add vào danh sách không,
   * khi đang lọc theo chia hội thoại (manager view).
   * Port từ FE13 `splitConversation.service.ts hasAddRecordFilter()`.
   */
  public hasAddRecordFilter(
    conversation: { assigneeIds?: string[]; assignedGroupId?: string },
    params: { divisionType?: number; assigneeIds?: string[]; assignedGroupId?: string },
  ): boolean {
    const assigneeIds = Array.isArray(conversation.assigneeIds) ? conversation.assigneeIds : [];
    const assignedGroupId = conversation?.assignedGroupId ?? '';
    // Lọc bỏ các cờ hệ thống để xác định nhân viên được gán thực tế
    const realAssigneeIds = assigneeIds.filter(id => !SYSTEM_ASSIGNEE_IDS.includes(id));

    if (params?.divisionType && params.divisionType === 1) {
      // Đang lọc hội thoại chưa được phân công cho nhân viên nào
      if (!realAssigneeIds.length) return true;
    }
    if (params?.assignedGroupId && assignedGroupId === params.assignedGroupId) {
      return true;
    }
    if (Array.isArray(params.assigneeIds) && params.assigneeIds.length) {
      if (realAssigneeIds.length) {
        return realAssigneeIds.some(a => params.assigneeIds!.includes(String(a)));
      }
    }
    return false;
  }

  /**
   * Kiểm tra hội thoại đến từ socket có được xóa khỏi danh sách không,
   * khi đang lọc theo chia hội thoại (manager view).
   * Port từ FE13 `splitConversation.service.ts hasRemoveRecordFilter()`.
   */
  public hasRemoveRecordFilter(
    conversation: { assigneeIds?: (string | number)[]; assignedGroupId?: string | number },
    params: { divisionType?: number | string; assigneeIds?: (string | number)[]; assignedGroupId?: string | number },
  ): boolean {
    let isRemove = false;
    const rawAssigneeIds = Array.isArray(conversation?.assigneeIds) ? conversation.assigneeIds.map(String) : [];
    // Lọc bỏ các cờ hệ thống để xác định danh sách nhân viên thực tế
    const realAssigneeIds = rawAssigneeIds.filter(id => !SYSTEM_ASSIGNEE_IDS.includes(id));

    // 1. Kiểm tra lọc theo trạng thái phân công (divisionType)
    if (params?.divisionType !== undefined && params.divisionType !== null && params.divisionType !== '') {
      const divType = Number(params.divisionType);
      if (divType === 1) {
        // Đang lọc "Chưa phân công": nếu hội thoại có nhân viên thực tế -> cần xóa
        if (realAssigneeIds.length > 0) {
          isRemove = true;
        }
      } else if (divType === 2) {
        // Đang lọc "Đã phân công": nếu hội thoại bị gỡ hết nhân viên thực tế -> cần xóa
        if (realAssigneeIds.length === 0) {
          isRemove = true;
        }
      }
    }

    // 2. Kiểm tra lọc theo nhân viên cụ thể (assigneeIds)
    if (!isRemove && Array.isArray(params?.assigneeIds) && params.assigneeIds.length > 0) {
      const filterAssigneeIds = params.assigneeIds.map(String);
      // Hội thoại phải có ít nhất một nhân viên nằm trong bộ lọc; nếu không có ai -> cần xóa
      const hasMatchedStaff = realAssigneeIds.some(id => filterAssigneeIds.includes(id));
      if (!hasMatchedStaff) {
        isRemove = true;
      }
    }

    // 3. Kiểm tra lọc theo nhóm phân công (assignedGroupId)
    if (!isRemove && params?.assignedGroupId !== undefined && params.assignedGroupId !== null && params.assignedGroupId !== '') {
      const filterGroupId = String(params.assignedGroupId);
      const convGroupId = String(conversation?.assignedGroupId ?? '');
      // Nếu đang lọc theo nhóm mà hội thoại không thuộc nhóm đó (bị gỡ nhóm hoặc đổi nhóm) -> cần xóa
      if (convGroupId !== filterGroupId) {
        isRemove = true;
      }
    }

    return isRemove;
  }

  public getModeSplitByPageId(pageId: string): number | null {
    if (!this.modeByBusiness || !Object.keys(this.modeByBusiness).length || !pageId) {
      return null;
    }
    const businessId = this.getBusinessIdByPageId(pageId);
    if (businessId && this.modeByBusiness[businessId]) {
      return this.modeByBusiness[businessId];
    }
    return null;
  }

  /**
   * Lấy businessId từ pageId thông qua AuthService.
   * Tương đương FE13: authService.getBussinessId(pageId).
   */
  private getBusinessIdByPageId(pageId: string): string | null {
    const pages = this.authService.getPages();
    const page = pages.find(p => String(p.id) === String(pageId));
    return page ? String((page as any).businessId ?? '') : null;
  }

  /**
   * [MSG-SEND-001] GAP-B: Tracking reply split conversation sau khi gửi tin.
   * FE13: splitConversation.service.ts trackingReplySplitConversation()
   * Gọi API để ghi log nhân viên đã xử lý hội thoại (phục vụ thống kê chia số).
   */
  public trackingReplySplitConversation(conversation: IConversation): void {
    const pageId = conversation.fanpageId;
    if (!pageId) return;
    if (this.checkManageBusiness(pageId)) return;
    if (!conversation.id || !conversation.assigneeIds?.length || !conversation.fanpageId) return;
    if (!conversation.assigneeIds.includes(this.authService.getUid())) return;

    const splitType = this.getModeSplitByPageId(conversation.fanpageId);
    if (!splitType) return;

    const params: Record<string, unknown> = {
      conversationId: conversation.id,
      splitType: String(splitType),
    };

    // FE13: Nếu có assignedGroupId, kiểm tra user có thuộc group không
    // (FE21 chưa port getUserByGroupId, skip group refinement)
    if (conversation.assignedGroupId) {
      params['assignedGroupId'] = conversation.assignedGroupId;
    }

    this.api.trackingReplySplitConversation(params as any).subscribe({
      next: () => {
      },
      error: () => {
      },
    });
  }

  // ── Helper dịch mã splitType (GAP 5) ──
  static readonly SPLIT_TYPE_NAMES: Record<number, string> = {
    1: 'Chia tự động (cá nhân)',
    2: 'Chia theo nhóm',
    3: 'Chuyển giao do quá hạn',
    4: 'Chuyển giao do đạt giới hạn',
    5: 'Quản lý chuyển thủ công',
    6: 'Nhân viên offline',
  };

  /**
   * Ánh xạ mã splitType sang tên tiếng Việt hiển thị trên UI
   */
  public getSplitTypeName(splitType: number | string): string {
    const type = Number(splitType);
    return SplitConversationService.SPLIT_TYPE_NAMES[type] || `Loại ${splitType}`;
  }

  // ── Join/Leave pool state (FE13 Split Conversation parity) ──
  /** Signal xác định có hiển thị nút/banner Nhận hoặc Tạm ngừng nhận hội thoại cho nhân viên hay không */
  public readonly isShowJoinConversation = signal<boolean>(false);
  /** Signal lưu thời gian nhân viên bắt đầu nhận hội thoại (chuỗi ISO datetime / timestamp) hoặc rỗng '' nếu đang tạm ngừng */
  public readonly joined = signal<string>('');
  /** Signal đánh dấu trạng thái đang gửi request bật/tắt nhận hội thoại để disable nút tránh double click */
  public readonly loadingJoin = signal<boolean>(false);

  /**
   * Kiểm tra nhân viên đang đăng nhập có thuộc danh sách nhận chia hội thoại hay không.
   * Logic tương đương bản v1 (FE13 SplitConversationService.checkJoinConversation):
   * - Bước 1: Kiểm tra tính hợp lệ của danh sách page đang chọn (nếu chưa chọn page thì bỏ qua).
   * - Bước 2: Lấy toàn bộ danh sách doanh nghiệp mà nhân viên thuộc về (dùng getBusinesses() thay vì getBusinessesManagement()).
   * - Bước 3: Gom nhóm danh sách pageId theo từng businessId dưới dạng: { [businessId]: [pageId1, pageId2, ...] }.
   * - Bước 4: Gọi API POST /user/checkjoinconversation lên Backend để kiểm tra cấu hình chia và trạng thái nhận của nhân viên.
   * - Bước 5: Cập nhật state isShowJoinConversation và joined dựa trên response trả về từ Backend.
   */
  /** Bộ page đang chờ kết quả /user/checkjoinconversation (dedupe các lượt gọi trùng). */
  private checkJoinInFlight: string | null = null;

  public checkJoinConversation(): void {
    // Bước 1: Kiểm tra danh sách page hiện tại của user, nếu chưa chọn page thì không gọi API
    const pageIds = this.authService.getPageIds();
    if (!pageIds || !Array.isArray(pageIds) || pageIds.length === 0) {
      return;
    }

    // Bước 2: Lấy toàn bộ danh sách doanh nghiệp user có quyền (dành cho cả tài khoản nhân viên không có quyền quản lý)
    const businesses = this.authService.getBusinesses() as any[];
    if (!businesses || !Array.isArray(businesses) || businesses.length === 0) {
      return;
    }

    // Bước 3: Gom nhóm danh sách ID trang theo từng mã doanh nghiệp
    const params: Record<string, string[]> = {};
    businesses.forEach((b: any) => {
      // Kiểm tra doanh nghiệp có ID và có danh sách trang hợp lệ
      if (b?.id && Array.isArray(b?.pages) && b.pages.length > 0) {
        const validPageIds: string[] = b.pages
          .map((p: any) => (typeof p === 'object' && p?.id != null ? String(p.id) : String(p)))
          .filter((id: string) => id.trim().length > 0);

        if (validPageIds.length > 0) {
          params[String(b.id)] = validPageIds;
        }
      }
    });

    // Nếu không có doanh nghiệp nào có page hợp lệ thì dừng
    if (!Object.keys(params).length) {
      return;
    }

    // Bước 4: Gọi API kiểm tra trạng thái nhận chia hội thoại. App init, onAuthChanged (bắn 2 lần mỗi lượt) và trang
    // hội thoại gọi gần như cùng lúc → cùng bộ page đang chờ kết quả thì dùng chung lượt đó, không gọi thêm.
    const data = JSON.stringify(params);
    if (this.checkJoinInFlight === data) return;
    this.checkJoinInFlight = data;
    this.api.checkJoinConversation({ data }).pipe(
      finalize(() => {
        if (this.checkJoinInFlight === data) this.checkJoinInFlight = null;
      }),
    ).subscribe({
      next: (res: any) => {
        // Hỗ trợ cả 2 dạng response: trực tiếp từ BE { status, joined } hoặc bọc trong { data: { status, joined } }
        const payload = res?.data ?? res;
        const isEligible = Boolean(payload?.status ?? res?.status);

        if (isEligible) {
          // Bật hiển thị icon / banner cho phép nhân viên nhận hoặc tạm ngừng
          this.isShowJoinConversation.set(true);
          // Lưu thời điểm nhận hội thoại nếu nhân viên đang ở trạng thái nhận
          const joinedTime = String(payload?.joined ?? res?.joined ?? '');
          this.joined.set(joinedTime);
        } else {
          // Không thuộc danh sách chia hoặc doanh nghiệp không bật chia hội thoại
          this.isShowJoinConversation.set(false);
          this.joined.set('');
        }
      },
      error: (err: any) => {
        console.error('[SplitConversationService] Lỗi khi gọi checkJoinConversation:', err);
      }
    });
  }

  /**
   * Xử lý khi nhân viên click nút "Bắt đầu nhận" hoặc "Tạm ngừng nhận" hội thoại.
   * Logic tương đương bản v1 (FE13 SplitConversationService.handleUpdateJoinSplit):
   * - Nếu đang nhận (joined có giá trị) -> gọi API tạm ngừng nhận (stopReceivingConversation).
   * - Nếu đang tạm ngừng (joined rỗng) -> gọi API bắt đầu nhận (startReceivingConversation).
   */
  public handleUpdateJoinSplit(): void {
    // Nếu không thuộc danh sách hiển thị nút thì không xử lý
    if (!this.isShowJoinConversation()) {
      return;
    }

    // Bật cờ loading để chặn click liên tục
    this.loadingJoin.set(true);

    const isCurrentlyJoined = Boolean(this.joined());
    const obs$ = isCurrentlyJoined
      ? this.api.stopReceivingConversation()
      : this.api.startReceivingConversation();

    obs$.subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res;

        // Nếu bắt đầu nhận thành công: BE trả về thời gian joinReceiving
        if (payload?.joinReceiving || res?.joinReceiving) {
          this.joined.set(String(payload?.joinReceiving ?? res?.joinReceiving ?? ''));
        }

        // Nếu tạm ngừng nhận thành công: BE trả về cờ removeJoinConversationAt
        if (payload?.removeJoinConversationAt || res?.removeJoinConversationAt) {
          this.joined.set('');
        }
      },
      error: (err: any) => {
        console.error('[SplitConversationService] Lỗi khi cập nhật trạng thái nhận hội thoại:', err);
      },
      complete: () => {
        // Tắt cờ loading sau khi xử lý xong
        this.loadingJoin.set(false);
      },
    });
  }

  // ── Auto ReAssignee Background Process (FE13 Parity) ──
  /** Cờ dừng tiến trình chia lại khi không có DN nào cấu hình chia (code === 0) */
  private isStopReAssignee = false;
  /** Cờ đánh dấu đang gửi request lên BE chia lại để tránh gọi trùng lặp */
  private isRunningReAssignee = false;
  /** Quản lý timer định kỳ để tránh tạo lặp timer */
  private reAssigneeIntervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Tiến trình định kỳ 2 phút/lần (120.000ms) tự động chia lại hội thoại dưới client:
   * + Tự động chuyển người cho nhân viên khác khi hết thời gian
   * + Đưa về danh sách hàng đợi (queue)
   * + Tự động chia lại các hội thoại chưa được chia kể từ khi bật setting chia
   * Tương đương bản cũ v1: SplitConversationService.intervalReAssignee()
   */
  public intervalReAssignee(): void {
    // Nếu timer đã được tạo và đang chạy → không tạo thêm để tránh lặp request
    if (this.reAssigneeIntervalId != null) return;

    this.reAssigneeIntervalId = setInterval(() => {
      this.toProgressReAssignee();
    }, 120000);
  }

  /**
   * Dừng tiến trình chia lại hội thoại (dùng khi đăng xuất / outPages).
   */
  public stopReAssigneeInterval(): void {
    if (this.reAssigneeIntervalId != null) {
      clearInterval(this.reAssigneeIntervalId);
      this.reAssigneeIntervalId = null;
    }
  }

  /**
   * Reset cờ dừng chia lại hội thoại để cho phép tiến trình chạy lại.
   * Dùng khi chuyển doanh nghiệp, chọn page mới, hoặc lưu cài đặt chia mới.
   * Tương đương bản cũ v1: resetSplitData() -> this.isStop = false
   */
  public resetStopReAssignee(): void {
    this.isStopReAssignee = false;
  }

  /**
   * Gửi request chia lại hội thoại lên BE:
   * + code = 0: Không có DN nào cài setting chia hội thoại -> dừng, không cần chạy tiếp (isStop = true)
   * + code = 1: Có DN cài setting chia, đã chạy thành công
   * + isNext = 1: Còn tiếp hội thoại trong hàng đợi -> tiếp tục đệ quy toProgressReAssignee() cho đến khi hết
   * + isNext = 0: Đã chạy hết đợt hiện tại
   * Tương đương bản cũ v1: SplitConversationService.toProgressReAssignee()
   */
  public toProgressReAssignee(): void {
    if (this.isStopReAssignee || this.isRunningReAssignee) return;
    this.isRunningReAssignee = true;

    this.api.intervalReAssignee({}).subscribe({
      next: (res: any) => {
        this.isRunningReAssignee = false;
        if (res?.hasOwnProperty('code')) {
          if (res?.isNext) {
            this.toProgressReAssignee();
          }
        }
        if (res?.code === 0) {
          this.isStopReAssignee = true;
        }
      },
      error: () => {
        this.isRunningReAssignee = false;
      },
    });
  }

  /**
   * Chia lại hội thoại theo ID (từ Socket / Queue).
   * FE13: SplitConversationService.reAssigneeConversationByIds()
   */
  public reAssigneeConversationByIds(conversationIds: string[], pageId: string): void {
    if (!conversationIds?.length || !pageId) return;
    this.toProgressReById([...conversationIds], pageId);
  }

  private toProgressReById(conversationIds: string[], pageId: string): void {
    if (!conversationIds.length) return;
    const cId = conversationIds.pop()!;
    this.api.reAssigneeConversationByIds({ id: cId, pageId }).subscribe({
      next: (res: any) => {
        if (res?.code) {
          this.toProgressReById(conversationIds, pageId);
        }
      },
    });
  }

  /** Reset toàn bộ trạng thái khi chuyển account/page */
  public resetSplitData(): void {
    this.isStopReAssignee = false;
    this.isRunningReAssignee = false;
    this.isShowJoinConversation.set(false);
    this.joined.set('');
  }
}

